import React from 'react';
import { IFormSendReceipt } from '@components/Sura/SendReceipt/types';
import { Grid, Typography, Button, CircularProgress, TextField } from '@material-ui/core';
import useStyles from '@components/Sura/SendReceipt/styles';
import { observer } from 'mobx-react';
import { Controller, FormProvider, useForm, SubmitHandler } from 'react-hook-form';
import { isEmailValid, isPhoneNumber } from '@utils/validators';
import useStore from '@hooks/useStore';
import { FieldValues } from 'react-hook-form/dist/types';
import { PaymentMethod } from '@store/sura';
import useToast from '@yaydoo/react-components/lib/hooks/useToast';

interface FormPayload {
  input: string;
}

const FormSendReceipt: React.FC<IFormSendReceipt> = ({ type }) => {
  const { links, ui, sura } = useStore();
  const { control, formState, handleSubmit } = useForm();
  const { openToast } = useToast();
  const css = useStyles({ isMobile: ui.isMobileScreen });
  const externalRefId = links.paymentLink.externalRefId;
  const isLoading = (type === 'email' ? sura.isSendingEmail : sura.isSendingSms) || sura.flag;

  const methods = useForm<FormPayload>();

  const onSubmit = async (params: FormPayload) => {
    try {
      if (sura.paymentMethod === PaymentMethod.bankReference) {
        await sura.postBankReference(externalRefId, 'send', params.input);
        openToast({
          message: 'Referencia enviada',
          title: 'Ok',
          type: 'success',
        });
      } else if (sura.paymentMethod === 'send-reference') {
        const response = await sura.sharePaymentLink(
          params.input,
          externalRefId,
          links.paymentLink.paymentLinkId,
        );
        if (response === 200) {
          openToast({
            message: 'Enviado con exito',
            title: 'Ok',
            type: 'success',
          });
        } else {
          openToast({
            message: 'No se pudo enviar',
            title: 'Error',
            type: 'error',
          });
        }
      } else {
        await sura.postSendReceipt(externalRefId, type, params.input);
        openToast({
          message: 'Enviado con exito',
          title: 'Ok',
          type: 'success',
        });
      }
    } catch (e) {
      openToast({
        message: 'No se pudo enviar',
        title: 'Error',
        type: 'error',
      });
    }
  };

  return (
    <div className={css.card}>
      <Typography className={css.labelEmail}>
        {type === 'email' ? 'Ingresar el correo electrónico' : 'Ingresar el teléfono'}
      </Typography>
      <FormProvider {...methods}>
        <form>
          <Grid className={css.divControl}>
            <Controller
              control={control}
              name="input"
              rules={{
                required: true,
                validate: type === 'email' ? { email: isEmailValid } : { phone: isPhoneNumber },
              }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label={type === 'email' ? 'Correo electrónico' : 'Número telefónico'}
                  id="standard-basic"
                  fullWidth
                  required
                  className={css.textfield}
                  disabled={formState.isSubmitting}
                  error={fieldState.invalid}
                  InputProps={{
                    className: css.input,
                    startAdornment: type === 'sms' ? '+52 ' : '',
                  }}
                  inputProps={
                    type === 'email'
                      ? {}
                      : {
                          inputMode: 'numeric',
                          mask: '+52 99 9999 9999',
                          placeholder: '   Ingresa tu número a 10 dígitos',
                        }
                  }
                  type={type === 'email' ? 'email' : 'number'}
                  onInput={(e: any) => {
                    if (type === 'sms') {
                      e.target.value = Math.max(0, parseInt(e.target.value))
                        .toString()
                        .slice(0, 10);
                    }
                  }}
                />
              )}
            />
            <Button
              className={css.button}
              onClick={handleSubmit(onSubmit as SubmitHandler<FieldValues>)}
              disabled={isLoading}
            >
              {isLoading ? <CircularProgress size={18} className={css.progress} /> : 'Enviar'}
            </Button>
          </Grid>
        </form>
      </FormProvider>
    </div>
  );
};

export default observer(FormSendReceipt);
